<!--
  SAX Editor Tab - Main Editor Interface

  FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/SaxEditorTab.vue

  Main editor interface with three-panel layout (palette | canvas | properties)
  Integrates into CategoryDocumentationDialog as a tab
-->

<template>
  <div class="sax-editor-tab">
    <!-- Toolbar -->
    <q-toolbar class="bg-white text-grey-9 shadow-1">
      <q-toolbar-title class="text-subtitle1">
        <q-icon name="account_tree" size="sm" class="q-mr-sm" />
        SAX Program Editor
        <q-badge v-if="hasUnsavedChanges" color="orange" class="q-ml-sm">
          Unsaved
        </q-badge>
        <q-badge v-if="isReadonly" color="grey" class="q-ml-sm">
          Read-Only
        </q-badge>
      </q-toolbar-title>

      <q-space />

      <!-- Main Actions -->
      <q-btn-group flat>
        <q-btn
          icon="undo"
          flat
          dense
          :disable="!canUndo || isReadonly"
          @click="handleUndo"
        >
          <q-tooltip>Undo (Ctrl+Z)</q-tooltip>
        </q-btn>

        <q-btn
          icon="redo"
          flat
          dense
          :disable="!canRedo || isReadonly"
          @click="handleRedo"
        >
          <q-tooltip>Redo (Ctrl+Y)</q-tooltip>
        </q-btn>

        <q-separator vertical inset />

        <q-btn
          icon="upload_file"
          flat
          dense
          :disable="isReadonly"
          @click="showImportDialog = true"
        >
          <q-tooltip>Import SAX File</q-tooltip>
        </q-btn>

        <q-btn
          icon="download"
          flat
          dense
          @click="handleExport"
        >
          <q-tooltip>Export SAX File</q-tooltip>
        </q-btn>

        <q-separator vertical inset />

        <q-btn
          icon="save"
          flat
          dense
          color="primary"
          :disable="!hasUnsavedChanges || isReadonly"
          :loading="isSaving"
          @click="handleSave(false)"
        >
          <q-tooltip>Save (Ctrl+S)</q-tooltip>
        </q-btn>

        <q-btn
          icon="history"
          flat
          dense
          @click="showVersionHistory = true"
        >
          <q-tooltip>Version History</q-tooltip>
        </q-btn>
      </q-btn-group>

      <q-separator vertical inset class="q-mx-sm" />

      <!-- Additional Tools -->
      <q-btn-group flat>
        <q-btn
          icon="link"
          flat
          dense
          @click="showPointMapping = true"
        >
          <q-tooltip>Point Mappings</q-tooltip>
        </q-btn>

        <q-btn
          icon="library_books"
          flat
          dense
          @click="showComponentLibrary = true"
        >
          <q-tooltip>Component Library</q-tooltip>
        </q-btn>

        <q-btn
          icon="check_circle"
          flat
          dense
          @click="handleValidate"
        >
          <q-tooltip>Validate Program</q-tooltip>
        </q-btn>
      </q-btn-group>
    </q-toolbar>

    <!-- Main Content Area -->
    <div class="sax-editor-content">
      <q-splitter
        v-model="leftSplitter"
        :limits="[15, 35]"
        class="sax-editor-splitter"
      >
        <!-- Left Panel: Stacked Structure and Components -->
        <template #before>
          <div class="sax-panel sax-panel-left column">
            <!-- Structure Section (Top) -->
            <div class="col-auto" style="max-height: 40%; overflow-y: auto; border-bottom: 1px solid #e0e0e0;">
              <div class="q-pa-sm">
                <div class="row items-center q-mb-sm">
                  <q-icon name="folder_open" size="sm" class="q-mr-xs text-grey-7" />
                  <div class="text-subtitle2 text-grey-8">Structure</div>
                  <q-space />
                  <q-btn
                    icon="create_new_folder"
                    size="sm"
                    flat
                    dense
                    color="primary"
                    :disable="isReadonly"
                    @click="showNewFolderDialog = true"
                  >
                    <q-tooltip>Create New Folder</q-tooltip>
                  </q-btn>
                </div>

                <!-- Breadcrumb Navigation -->
                <div class="q-mb-sm">
                  <q-breadcrumbs>
                    <q-breadcrumbs-el
                      :label="equipmentName"
                      icon="home"
                      class="cursor-pointer"
                      @click="navigateToFolder(null)"
                    />
                    <q-breadcrumbs-el
                      v-for="folder in currentFolderPath"
                      :key="folder.id"
                      :label="folder.name"
                      class="cursor-pointer"
                      @click="navigateToFolder(folder.id)"
                    />
                  </q-breadcrumbs>
                </div>

                <!-- Hierarchical Tree View -->
                <q-tree
                  v-model:expanded="expandedFolders"
                  :nodes="hierarchyTreeNodes"
                  node-key="id"
                  no-nodes-label="No components yet"
                  dense
                >
                  <template #default-header="prop">
                    <div
                      class="row items-center full-width"
                      :class="{ 'bg-blue-1': prop.node.id === currentFolderId }"
                      @contextmenu.prevent="handleTreeNodeContextMenu($event, prop.node)"
                    >
                      <q-icon
                        :name="prop.node.isFolder ? 'folder' : 'widgets'"
                        :color="prop.node.isFolder ? 'amber-7' : 'blue-6'"
                        size="18px"
                        class="q-mr-sm"
                      />
                      <div
                        class="text-body2 cursor-pointer"
                        :class="{ 'text-weight-bold text-primary': prop.node.id === currentFolderId }"
                        style="flex: 1"
                        @click="handleTreeNodeClick(prop.node)"
                      >
                        {{ prop.node.label }}
                      </div>
                      <q-space />
                      <div v-if="prop.node.hasChildren" class="text-caption text-grey-6 q-mr-sm">
                        {{ prop.node.childCount }}
                      </div>
                    </div>
                  </template>
                </q-tree>
              </div>
            </div>

            <q-separator />

            <!-- Components Palette Section (Bottom) -->
            <div class="col">
              <div class="q-pa-sm">
                <div class="row items-center q-mb-sm">
                  <q-icon name="widgets" size="sm" class="q-mr-xs text-grey-7" />
                  <div class="text-subtitle2 text-grey-8">Components</div>
                </div>
              </div>
              <SaxComponentPalette
                :equipment-id="props.equipmentId"
                :equipment-name="equipmentName"
                :readonly="isReadonly"
                @component-selected="handleComponentSelected"
              />
            </div>
          </div>
        </template>

        <!-- Center and Right Panels -->
        <template #after>
          <q-splitter
            v-model="rightSplitter"
            :limits="[50, 85]"
          >
            <!-- Center Panel: Canvas -->
            <template #before>
              <div class="sax-panel sax-panel-center">
                <SaxVisualCanvas
                  ref="canvasRef"
                  :equipment="equipment"
                  :site-id="siteId"
                  :readonly="isReadonly"
                  :current-folder-id="currentFolderId"
                  @selection-changed="handleSelectionChanged"
                />
              </div>
            </template>

            <!-- Right Panel: Properties -->
            <template #after>
              <div class="sax-panel sax-panel-right">
                <SaxPropertiesPanel
                  :selected-items="selectedItems"
                  :readonly="isReadonly"
                  @property-changed="handlePropertyChanged"
                />
              </div>
            </template>
          </q-splitter>
        </template>
      </q-splitter>
    </div>

    <!-- Status Bar -->
    <q-toolbar class="bg-grey-2 text-grey-8" style="min-height: 32px;">
      <div class="text-caption">
        <q-icon name="widgets" size="xs" class="q-mr-xs" />
        {{ componentCount }} component{{ componentCount !== 1 ? 's' : '' }}

        <q-separator vertical inset class="q-mx-sm" />

        <q-icon name="cable" size="xs" class="q-mr-xs" />
        {{ linkCount }} link{{ linkCount !== 1 ? 's' : '' }}

        <q-separator vertical inset class="q-mx-sm" />

        <q-icon name="filter_center_focus" size="xs" class="q-mr-xs" />
        {{ selectedItems.length }} selected
      </div>

      <q-space />

      <div v-if="autoSaveEnabled" class="text-caption">
        <q-icon name="cloud_done" size="xs" class="q-mr-xs" />
        Auto-save enabled
        <span v-if="lastSavedAt" class="text-grey-6 q-ml-xs">
          ({{ lastSavedAt }})
        </span>
      </div>
    </q-toolbar>

    <!-- Import Dialog -->
    <SaxImportDialog
      v-model="showImportDialog"
      :equipment-id="equipmentId"
      :site-id="siteId"
      @import-complete="handleImportComplete"
    />

    <!-- Version History Panel -->
    <VersionHistoryPanel
      v-model="showVersionHistory"
      :equipment-id="equipmentId"
      :site-id="siteId"
      @version-restored="handleVersionRestored"
    />

    <!-- Point Mapping Panel -->
    <PointMappingPanel
      v-model="showPointMapping"
      :equipment-id="equipmentId"
      :site-id="siteId"
    />

    <!-- Component Library Manager -->
    <ComponentLibraryManager
      v-model="showComponentLibrary"
    />

    <!-- Controller Read-Only View (if equipment type is controller) -->
    <ControllerReadOnlyView
      v-if="isController"
      :equipment-id="equipmentId"
      :site-id="siteId"
    />

    <!-- New Folder Dialog -->
    <q-dialog v-model="showNewFolderDialog">
      <q-card style="min-width: 400px">
        <q-card-section>
          <div class="text-h6">Create New Folder</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-input
            v-model="newFolderName"
            label="Folder Name"
            dense
            outlined
            autofocus
            @keyup.enter="createNewFolder"
          >
            <template #prepend>
              <q-icon name="folder" />
            </template>
          </q-input>
          <div class="text-caption text-grey-6 q-mt-xs">
            The folder will be created {{ currentFolderId ? 'in the current folder' : 'at the root level' }}
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup label="Cancel" flat color="grey" />
          <q-btn
            label="Create"
            color="primary"
            :disable="!newFolderName.trim()"
            @click="createNewFolder"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Tree Context Menu -->
    <q-menu
      v-model="showTreeContextMenu"
      context-menu
      transition-show="jump-down"
      transition-hide="jump-up"
    >
      <q-list dense style="min-width: 150px">
        <q-item v-close-popup clickable @click="handleTreeRename">
          <q-item-section avatar>
            <q-icon name="edit" />
          </q-item-section>
          <q-item-section>Rename</q-item-section>
        </q-item>
        <q-item v-close-popup clickable @click="handleTreeDuplicate">
          <q-item-section avatar>
            <q-icon name="content_copy" />
          </q-item-section>
          <q-item-section>Duplicate</q-item-section>
        </q-item>
        <q-separator />
        <q-item v-close-popup clickable class="text-negative" @click="handleTreeDelete">
          <q-item-section avatar>
            <q-icon name="delete" color="negative" />
          </q-item-section>
          <q-item-section>Delete</q-item-section>
        </q-item>
      </q-list>
    </q-menu>

    <!-- Tree Rename Dialog -->
    <q-dialog v-model="showTreeRenameDialog">
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">Rename Component</div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-input
            v-model="treeRenameValue"
            label="Component Name"
            dense
            outlined
            autofocus
            @keyup.enter="confirmTreeRename"
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup label="Cancel" flat color="grey" />
          <q-btn
            label="Rename"
            color="primary"
            :disable="!treeRenameValue.trim()"
            @click="confirmTreeRename"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useQuasar } from 'quasar';
  import { useSaxEditorStore } from '@/stores/saxEditor';
  import { storeToRefs } from 'pinia';
  import SaxVisualCanvas from './SaxVisualCanvas.vue';
  import SaxComponentPalette from './SaxComponentPalette.vue';
  import SaxPropertiesPanel from './SaxPropertiesPanel.vue';
  import SaxImportDialog from './SaxImportDialog.vue';
  import VersionHistoryPanel from './VersionHistoryPanel.vue';
  import PointMappingPanel from './PointMappingPanel.vue';
  import ComponentLibraryManager from './ComponentLibraryManager.vue';
  import ControllerReadOnlyView from './ControllerReadOnlyView.vue';

  // Props
  const props = defineProps({
    equipmentId: {
      type: Number,
      required: true,
    },
    equipment: {
      type: Object,
      default: null,
    },
    siteId: {
      type: Number,
      default: null,
    },
    equipmentType: {
      type: String,
      default: null, // 'ahu', 'vav', 'fcu', 'controller', 'zone'
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    autoSave: {
      type: Boolean,
      default: true,
    },
  });

  // Emits
  const emit = defineEmits(['data-loaded', 'data-saved', 'error']);

  // Quasar
  const $q = useQuasar();

  // Store
  const saxStore = useSaxEditorStore();
  const {
    currentSaxData,
    selectedComponents,
    selectedLinks,
    canUndo,
    canRedo,
    isDirty,
  } = storeToRefs(saxStore);

  // Refs
  const canvasRef = ref(null);
  const leftSplitter = ref(20); // 20% for palette
  const rightSplitter = ref(75); // 75% for canvas, 25% for properties
  // Removed leftPanelTab - now using stacked layout instead of tabs
  const currentFolderId = ref(null); // Currently navigated folder (null = root)
  const expandedFolders = ref([]); // Expanded folder IDs in tree

  // Dialog visibility
  const showImportDialog = ref(false);
  const showVersionHistory = ref(false);
  const showPointMapping = ref(false);
  const showComponentLibrary = ref(false);
  const showNewFolderDialog = ref(false);

  // New folder state
  const newFolderName = ref('');

  // Tree context menu state
  const showTreeContextMenu = ref(false);
  const treeContextMenuTarget = ref(null);
  const showTreeRenameDialog = ref(false);
  const treeRenameValue = ref('');

  // State
  const isSaving = ref(false);
  const isLoading = ref(false);
  const lastSavedAt = ref(null);
  const autoSaveTimer = ref(null);

  // Computed
  const isReadonly = computed(() => {
    return props.readonly || props.equipmentType === 'controller';
  });

  const isController = computed(() => {
    return props.equipmentType === 'controller';
  });

  const hasUnsavedChanges = computed(() => {
    return isDirty.value;
  });

  const autoSaveEnabled = computed(() => {
    return props.autoSave && !isReadonly.value;
  });

  const equipmentName = computed(() => {
    // Use actual equipment name from props, fallback to Equipment ID
    return props.equipment?.name || `Equipment ${props.equipmentId}`;
  });

  const selectedItems = computed(() => {
    return [
      ...(selectedComponents.value || []).map((id) => ({
        type: 'component',
        id,
        data: currentSaxData.value?.components?.find((c) => c.id === id),
      })),
      ...(selectedLinks.value || []).map((id) => ({
        type: 'link',
        id,
        data: currentSaxData.value?.links?.find((l) => l.id === id),
      })),
    ];
  });

  const componentCount = computed(() => {
    if (!currentSaxData.value?.components) {return 0;}

    // Count only components in current folder view
    return currentSaxData.value.components.filter((c) => c.parent_id === currentFolderId.value).length;
  });

  const linkCount = computed(() => {
    if (!currentSaxData.value?.links) {return 0;}

    // Count only links between components in current folder view
    const componentsInFolder = currentSaxData.value.components
      .filter((c) => c.parent_id === currentFolderId.value)
      .map((c) => c.id);

    return currentSaxData.value.links.filter((l) =>
      componentsInFolder.includes(l.from_component) &&
      componentsInFolder.includes(l.to_component),
    ).length;
  });

  /**
   * Build hierarchical tree showing all components with expand/collapse
   */
  const hierarchyTreeNodes = computed(() => {
    const components = currentSaxData.value?.components || [];

    function buildTree(parentId = null) {
      const children = components.filter((c) => c.parent_id === parentId);

      return children.map((component) => {
        const childNodes = buildTree(component.id);
        const childCount = components.filter((c) => c.parent_id === component.id).length;

        return {
          id: component.id,
          label: component.name,
          type: component.type,
          isFolder: component.type === 'sys::Folder',
          hasChildren: childCount > 0,
          childCount: childCount,
          children: childNodes.length > 0 ? childNodes : undefined,
        };
      });
    }

    return buildTree(null);
  });

  /**
   * Get breadcrumb path for current folder
   */
  const currentFolderPath = computed(() => {
    if (currentFolderId.value === null) {return [];}

    const components = currentSaxData.value?.components || [];
    const breadcrumbs = [];

    let current = components.find((c) => c.id === currentFolderId.value);
    while (current) {
      breadcrumbs.unshift({ id: current.id, name: current.name });
      current = components.find((c) => c.id === current.parent_id);
    }

    return breadcrumbs;
  });

  /**
   * Get child count for a component/folder
   */
  function getChildCount(componentId) {
    const components = currentSaxData.value?.components || [];
    return components.filter((c) => c.parent_id === componentId).length;
  }

  /**
   * Navigate to a folder (or component that acts as a folder)
   */
  function navigateToFolder(folderId) {
    currentFolderId.value = folderId;
  }

  /**
   * Handle clicking on a tree node (folder or component)
   * - If clicking a folder: navigate into it
   * - If clicking a component: navigate to its parent folder to show it
   */
  function handleTreeNodeClick(node) {
    if (node.isFolder) {
      // Navigate into the folder
      navigateToFolder(node.id);
    } else {
      // For components, navigate to their parent folder to show them
      const components = currentSaxData.value?.components || [];
      const component = components.find((c) => c.id === node.id);
      if (component) {
        navigateToFolder(component.parent_id);
      }
    }
  }

  /**
   * Create a new folder
   */
  function createNewFolder() {
    const folderName = newFolderName.value.trim();
    if (!folderName) {return;}

    // Check for duplicate name
    const components = currentSaxData.value?.components || [];
    const isDuplicate = components.some((c) => c.name === folderName);
    if (isDuplicate) {
      $q.notify({
        type: 'negative',
        message: `A component named "${folderName}" already exists`,
      });
      return;
    }

    // Create new folder component in the current folder
    const newFolder = {
      id: `folder_${Date.now()}`,
      name: folderName,
      type: 'sys::Folder',
      kit: 'sys',
      kit_name: 'sys',
      parent_id: currentFolderId.value || null,
      position: { x: 100, y: 100 },
      width: 200,
      height: 150,
      input_slots: [],
      output_slots: [],
      config_values: {},
    };

    // Add to store
    saxStore.addComponent(newFolder);

    // Notify user
    $q.notify({
      type: 'positive',
      message: `Folder "${folderName}" created`,
      icon: 'folder',
    });

    // Reset dialog
    newFolderName.value = '';
    showNewFolderDialog.value = false;
  }

  /**
   * Handle tree node context menu
   */
  function handleTreeNodeContextMenu(event, item) {
    treeContextMenuTarget.value = item.id;
    showTreeContextMenu.value = true;
  }

  /**
   * Handle rename from tree context menu
   */
  function handleTreeRename() {
    const component = saxStore.currentSaxData.components.find((c) => c.id === treeContextMenuTarget.value);
    if (component) {
      treeRenameValue.value = component.name;
      showTreeRenameDialog.value = true;
    }
  }

  /**
   * Confirm rename from tree
   */
  function confirmTreeRename() {
    const newName = treeRenameValue.value.trim();
    if (!newName) {
      $q.notify({ type: 'negative', message: 'Component name cannot be empty' });
      return;
    }

    const isDuplicate = saxStore.currentSaxData.components.some(
      (c) => c.id !== treeContextMenuTarget.value && c.name === newName,
    );
    if (isDuplicate) {
      $q.notify({ type: 'negative', message: `Component name "${newName}" already exists` });
      return;
    }

    saxStore.updateComponent(treeContextMenuTarget.value, { name: newName });

    $q.notify({
      type: 'positive',
      message: 'Component renamed',
      icon: 'edit',
    });

    showTreeRenameDialog.value = false;
  }

  /**
   * Handle duplicate from tree context menu
   */
  function handleTreeDuplicate() {
    const component = saxStore.currentSaxData.components.find((c) => c.id === treeContextMenuTarget.value);
    if (!component) {return;}

    const baseName = component.name.replace(/_\d+$/, '');
    let copyNumber = 1;
    let newName = `${baseName}_${copyNumber}`;

    while (saxStore.currentSaxData.components.some((c) => c.name === newName)) {
      copyNumber++;
      newName = `${baseName}_${copyNumber}`;
    }

    const newComponent = {
      ...component,
      id: `component_${Date.now()}`,
      name: newName,
      position: { x: component.position.x + 20, y: component.position.y + 20 },
    };

    saxStore.addComponent(newComponent);

    $q.notify({
      type: 'positive',
      message: `Component duplicated as "${newName}"`,
      icon: 'content_copy',
    });
  }

  /**
   * Handle delete from tree context menu
   */
  function handleTreeDelete() {
    const component = saxStore.currentSaxData.components.find((c) => c.id === treeContextMenuTarget.value);
    if (!component) {return;}

    $q.dialog({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete "${component.name}"?`,
      cancel: true,
      persistent: true,
    }).onOk(() => {
      saxStore.deleteComponent(treeContextMenuTarget.value);

      $q.notify({
        type: 'positive',
        message: 'Component deleted',
        icon: 'delete',
      });
    });
  }

  // Methods

  /**
   * Load SAX data for equipment
   */
  async function loadData() {
    isLoading.value = true;
    try {
      await saxStore.loadSaxDataForEquipment(props.equipmentId, props.siteId);
      emit('data-loaded', currentSaxData.value);
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load SAX data',
        caption: error.message,
      });
      emit('error', error);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Save SAX data
   */
  async function handleSave(createVersion = false) {
    if (isReadonly.value) {return;}

    isSaving.value = true;
    try {
      const response = await saxStore.saveSaxData(
        props.equipmentId,
        createVersion,
        createVersion ? 'Manual save' : null,
        props.siteId,
      );

      lastSavedAt.value = new Date().toLocaleTimeString();

      $q.notify({
        type: 'positive',
        message: 'SAX program saved',
        icon: 'save',
        timeout: 1500,
      });

      // Emit with tab_completeness if returned from API
      emit('data-saved', currentSaxData.value, response?.tab_completeness);
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to save SAX data',
        caption: error.message,
      });
      emit('error', error);
    } finally {
      isSaving.value = false;
    }
  }

  /**
   * Auto-save with debouncing
   */
  function setupAutoSave() {
    if (!autoSaveEnabled.value) {return;}

    if (autoSaveTimer.value) {
      clearTimeout(autoSaveTimer.value);
    }

    autoSaveTimer.value = setTimeout(() => {
      if (hasUnsavedChanges.value) {
        handleSave(false);
      }
    }, 2000); // 2 second debounce
  }

  /**
   * Undo last action
   */
  function handleUndo() {
    saxStore.undo();
  }

  /**
   * Redo last undone action
   */
  function handleRedo() {
    saxStore.redo();
  }

  /**
   * Generate SAX XML from current data
   */
  function generateSaxXml() {
    const saxData = saxStore.currentSaxData;
    if (!saxData || !saxData.components) {
      throw new Error('No SAX data to export');
    }

    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<sedona>\n';
    xml += '  <schema>\n';

    /**
     * Recursively build component hierarchy
     */
    function buildComponent(component, indent, parentPath = '') {
      let xml = '';
      const componentPath = parentPath ? `${parentPath}.${component.name}` : component.name;

      // Find children of this component
      const children = saxData.components.filter((c) => c.parent_id === component.id);
      const configValues = component.config_values || {};
      const hasConfig = Object.keys(configValues).length > 0;
      const hasChildren = children.length > 0;

      // Open tag
      xml += `${indent}<comp name="${escapeXml(component.name)}" type="${escapeXml(component.type)}"`;

      if (hasConfig || hasChildren) {
        xml += '>\n';

        // Add config properties
        if (hasConfig) {
          Object.entries(configValues).forEach(([propName, propValue]) => {
            xml += `${indent}  <prop name="${escapeXml(propName)}" val="${escapeXml(String(propValue))}"/>\n`;
          });
        }

        // Add children recursively
        if (hasChildren) {
          children.forEach((child) => {
            xml += buildComponent(child, indent + '  ', componentPath);
          });
        }

        xml += `${indent}</comp>\n`;
      } else {
        xml += '/>\n';
      }

      return xml;
    }

    // Create virtual root folder for equipment (only in XML, not in data)
    const equipmentFolderName = equipmentName.value.replace(/\s+/g, '_');
    xml += `    <comp name="${escapeXml(equipmentFolderName)}" type="sys::Folder">\n`;

    // Build hierarchy starting from root components (no parent)
    const rootComponents = saxData.components.filter((c) => !c.parent_id);
    rootComponents.forEach((component) => {
      xml += buildComponent(component, '      ', equipmentFolderName);
    });

    xml += '    </comp>\n';

    xml += '  </schema>\n';

    // Add links section
    if (saxData.links && saxData.links.length > 0) {
      xml += '  <links>\n';

      /**
       * Get full qualified path for component
       */
      function getComponentPath(compId) {
        const comp = saxData.components.find((c) => c.id === compId);
        if (!comp) {return '';}

        const path = [comp.name];
        let current = comp;

        while (current.parent_id) {
          const parent = saxData.components.find((c) => c.id === current.parent_id);
          if (!parent) {break;}
          path.unshift(parent.name);
          current = parent;
        }

        return path.join('.');
      }

      saxData.links.forEach((link) => {
        const fromPath = getComponentPath(link.from_component);
        const toPath = getComponentPath(link.to_component);

        if (fromPath && toPath) {
          // Prepend equipment folder name to paths
          const fullFromPath = `${equipmentFolderName}.${fromPath}`;
          const fullToPath = `${equipmentFolderName}.${toPath}`;
          xml += `    <link from="${escapeXml(fullFromPath)}.${escapeXml(link.from_slot)}" `;
          xml += `to="${escapeXml(fullToPath)}.${escapeXml(link.to_slot)}"/>\n`;
        }
      });

      xml += '  </links>\n';
    }

    xml += '</sedona>\n';

    return xml;
  }

  /**
   * Escape XML special characters
   */
  function escapeXml(text) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  /**
   * Export SAX file
   */
  async function handleExport() {
    try {
      // Generate XML
      const xmlContent = generateSaxXml();

      // Create blob and download
      const blob = new Blob([xmlContent], { type: 'text/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${saxStore.currentSaxData.metadata?.name || 'program'}.sax`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      $q.notify({
        type: 'positive',
        message: 'SAX file exported',
        icon: 'download',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to export SAX file',
        caption: error.message,
      });
    }
  }

  /**
   * Handle import complete
   */
  function handleImportComplete(importedData) {
    $q.notify({
      type: 'positive',
      message: 'SAX file imported successfully',
      caption: `${importedData.components?.length || 0} components, ${importedData.links?.length || 0} links`,
      icon: 'upload_file',
    });
  }

  /**
   * Handle version restored
   */
  function handleVersionRestored(version) {
    loadData();

    $q.notify({
      type: 'positive',
      message: 'Version restored',
      caption: `Restored to version ${version.version_number}`,
      icon: 'restore',
    });
  }

  /**
   * Validate program
   */
  async function handleValidate() {
    try {
      const validation = await saxStore.validateProgram(props.equipmentId, props.siteId);

      if (validation.is_valid) {
        $q.notify({
          type: 'positive',
          message: 'Program is valid',
          icon: 'check_circle',
        });
      } else {
        // Show validation errors
        $q.dialog({
          title: 'Validation Errors',
          message: validation.errors.join('\n'),
          html: true,
        });
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Validation failed',
        caption: error.message,
      });
    }
  }

  /**
   * Handle component selected from palette
   */
  function handleComponentSelected(componentType) {
    // Component will be added via drag-and-drop on canvas
  }

  /**
   * Handle selection changed on canvas
   */
  function handleSelectionChanged(selection) {
    // Selection is managed by store, just for reference
  }

  /**
   * Handle property changed
   */
  function handlePropertyChanged({ itemId, itemType, property, value }) {
    if (itemType === 'component') {
      saxStore.updateComponent(itemId, { [property]: value });
    } else if (itemType === 'link') {
      // Links are currently read-only in the properties panel
      console.warn('Link property updates not yet implemented');
    }
  }

  /**
   * Keyboard shortcuts
   */
  function handleKeyDown(e) {
    // Don't trigger shortcuts if user is typing in an input field
    const isInputField = e.target.tagName === 'INPUT' ||
      e.target.tagName === 'TEXTAREA' ||
      e.target.isContentEditable;

    // Ctrl+S - Save
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      if (!isReadonly.value) {
        handleSave();
      }
    }

    // Ctrl+Z - Undo
    if (e.ctrlKey && e.key === 'z') {
      e.preventDefault();
      if (canUndo.value && !isReadonly.value) {
        handleUndo();
      }
    }

    // Ctrl+Y - Redo
    if (e.ctrlKey && e.key === 'y') {
      e.preventDefault();
      if (canRedo.value && !isReadonly.value) {
        handleRedo();
      }
    }

    // Delete - Delete selected (only if not in input field)
    if ((e.key === 'Delete' || e.key === 'Backspace') && !isInputField) {
      if (!isReadonly.value && selectedItems.value.length > 0) {
        e.preventDefault();
        selectedItems.value.forEach((item) => {
          if (item.type === 'component') {
            saxStore.deleteComponent(item.id);
          } else if (item.type === 'link') {
            saxStore.deleteLink(item.id);
          }
        });
      }
    }

    // Ctrl+C - Copy
    if (e.ctrlKey && e.key === 'c') {
      if (selectedItems.value.length > 0) {
        e.preventDefault();
        saxStore.copySelection();
        $q.notify({
          type: 'info',
          message: `Copied ${selectedItems.value.length} item(s)`,
          timeout: 1000,
        });
      }
    }

    // Ctrl+V - Paste
    if (e.ctrlKey && e.key === 'v') {
      if (!isReadonly.value) {
        e.preventDefault();
        saxStore.paste();
        $q.notify({
          type: 'info',
          message: 'Pasted',
          timeout: 1000,
        });
      }
    }

    // Ctrl+A - Select all
    if (e.ctrlKey && e.key === 'a') {
      e.preventDefault();
      saxStore.selectAllComponents();
    }
  }

  // Watchers

  /**
   * Watch for changes to trigger auto-save
   */
  watch(
    () => currentSaxData.value,
    () => {
      if (autoSaveEnabled.value) {
        setupAutoSave();
      }
    },
    { deep: true },
  );

  // Lifecycle

  onMounted(() => {
    // Load initial data
    loadData();

    // Setup keyboard shortcuts
    window.addEventListener('keydown', handleKeyDown);

    // Setup auto-save
    if (autoSaveEnabled.value) {
      setupAutoSave();
    }
  });

  onBeforeUnmount(() => {
    // Cleanup
    window.removeEventListener('keydown', handleKeyDown);

    if (autoSaveTimer.value) {
      clearTimeout(autoSaveTimer.value);
    }

    // Save if there are unsaved changes
    if (hasUnsavedChanges.value && autoSaveEnabled.value) {
      handleSave(false);
    }
  });
</script>

<style scoped lang="scss">
.sax-editor-tab {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #f5f5f5;
}

.sax-editor-content {
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  min-height: 0;
  max-height: 100%;
}

.sax-editor-splitter {
  height: 100%;
  width: 100%;

  /* Ensure splitter slots get full height */
  :deep(.q-splitter__panel) {
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  :deep(.q-splitter__separator) {
    background: #e0e0e0;
  }
}

.sax-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: white;
  overflow: hidden; /* Clip to panel boundaries */
  position: relative;
}

.sax-panel-left {
  border-right: 1px solid #e0e0e0;
}

.sax-panel-center {
  background: #fafafa;
}

.sax-panel-right {
  border-left: 1px solid #e0e0e0;
}

// Responsive adjustments
@media (max-width: 1200px) {
  .sax-panel-left {
    min-width: 200px;
  }

  .sax-panel-right {
    min-width: 250px;
  }
}

@media (max-width: 768px) {
  .sax-editor-tab {
    .q-toolbar {
      .q-btn {
        padding: 4px 8px;
      }
    }
  }
}
</style>
